import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import { supabase } from '../lib/supabase'
import { getUnreadCount } from '../services/notifications'
import { useAuth } from './useAuth'
import type { Notification } from '../types/notifications'

// ============================================================
// Context shape
// ============================================================

interface NotificationsState {
  unreadCount: number
  notifications: Notification[]
  notificationsLoaded: boolean
  loadNotifications: () => Promise<void>
  pushNotification: (n: Notification) => void
  updateNotification: (updated: Notification) => void
  decrementUnread: () => void
  clearUnread: () => void
}

const NotificationsContext = createContext<NotificationsState | null>(null)

// ============================================================
// Provider — one instance per authenticated session.
// Manages polling, Realtime subscription, and shared state so
// multiple NotificationsDropdown instances (desktop + mobile nav)
// share a single fetch and a single Realtime channel.
// ============================================================

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()

  const [unreadCount, setUnreadCount] = useState(0)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [notificationsLoaded, setNotificationsLoaded] = useState(false)

  const loadUnreadCount = useCallback(async () => {
    if (!user?.id) return
    try {
      const count = await getUnreadCount()
      setUnreadCount(count)
    } catch {
      // Non-critical — polling will retry on next interval
    }
  }, [user?.id])

  const loadNotifications = useCallback(async () => {
    if (!user?.id) return
    setNotificationsLoaded(false)
    try {
      // Dynamic import avoids a circular dependency since notifications.ts
      // imports from auth.ts which is already in this hook's dep chain.
      const { getNotifications } = await import('../services/notifications')
      const data = await getNotifications(30)
      setNotifications(data)
    } catch {
      // Non-critical
    } finally {
      setNotificationsLoaded(true)
    }
  }, [user?.id])

  const pushNotification = useCallback((n: Notification) => {
    setNotifications(prev => [n, ...prev].slice(0, 30))
    setUnreadCount(prev => prev + 1)
  }, [])

  const updateNotification = useCallback((updated: Notification) => {
    setNotifications(prev => prev.map(n => (n.id === updated.id ? updated : n)))
    // Recalculate count rather than guess — a batch mark-all-read or other
    // external change could have happened.
    loadUnreadCount()
  }, [loadUnreadCount])

  const decrementUnread = useCallback(() => {
    setUnreadCount(prev => Math.max(0, prev - 1))
  }, [])

  const clearUnread = useCallback(() => {
    setUnreadCount(0)
  }, [])

  // ----------------------------------------------------------
  // Initial load + 60s polling fallback
  // ----------------------------------------------------------

  useEffect(() => {
    if (!user?.id) return
    loadUnreadCount()
    const interval = setInterval(loadUnreadCount, 60_000)
    return () => clearInterval(interval)
  }, [user?.id, loadUnreadCount])

  // ----------------------------------------------------------
  // Supabase Realtime subscription (single channel per session)
  // ----------------------------------------------------------

  useEffect(() => {
    if (!user?.id) return

    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        (payload) => pushNotification(payload.new as Notification)
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        (payload) => updateNotification(payload.new as Notification)
      )
      .subscribe((status, err) => {
        // Re-fetch count once connected to catch any notifications that
        // arrived during the connection handshake delay.
        if (status === 'SUBSCRIBED') loadUnreadCount()
        if (err) console.error('[Realtime] Notifications subscription error:', err)
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn('[Realtime] Notifications subscription failed, polling active:', status)
        }
      })

    return () => { supabase.removeChannel(channel) }
  }, [user?.id, loadUnreadCount, pushNotification, updateNotification])

  return (
    <NotificationsContext.Provider value={{
      unreadCount,
      notifications,
      notificationsLoaded,
      loadNotifications,
      pushNotification,
      updateNotification,
      decrementUnread,
      clearUnread,
    }}>
      {children}
    </NotificationsContext.Provider>
  )
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext)
  if (!ctx) throw new Error('useNotifications must be used within NotificationsProvider')
  return ctx
}
